import { useEffect } from 'react';

import a11yService from '../../services/a11yService';

export const ESSAY_RESTORE_CARD_ATTRIBUTE = 'data-essay-restore-card';

/** Focus before the first card opened, shared as the success card replaces the warning. */
let focusBeforeCards;

/** Parent walk: the player avoids Element.closest. */
function isInsideCard(element) {
    let node = element;
    while (node && node !== document) {
        if (node.hasAttribute && node.hasAttribute(ESSAY_RESTORE_CARD_ATTRIBUTE)) {
            return true;
        }
        node = node.parentNode;
    }
    return false;
}

const otherCardOpen = ownCard => Array.from(document.querySelectorAll(`[${ESSAY_RESTORE_CARD_ATTRIBUTE}]`))
    .some(card => card !== ownCard);

function focusIfPossible(element) {
    if (element && typeof element.focus === 'function') {
        element.focus();
        return true;
    }
    return false;
}

/** Covers what a11yService's trap does not: Tab from the card itself, or with no enabled action. */
function keepTabInside(event) {
    if (event.key !== 'Tab') {
        return;
    }
    const card = event.currentTarget;
    const actions = card.querySelectorAll('button:not([disabled])');
    if (!actions.length) {
        event.preventDefault();
        return;
    }
    if (event.target === card) {
        event.preventDefault();
        actions[event.shiftKey ? actions.length - 1 : 0].focus();
    }
}

/**
 * Modal focus for the restore cards (AC1.7): moves focus into the card, traps it, and returns it
 * once the last card closes.
 *
 * @param {object} cardRef the card's outer element (tabIndex -1)
 */
export default function useEssayRestoreModalFocus(cardRef) {
    useEffect(() => {
        const card = cardRef.current;
        if (!card) {
            return undefined;
        }

        const previous = document.activeElement;
        if (!focusBeforeCards && previous && previous !== document.body && !isInsideCard(previous)) {
            focusBeforeCards = previous;
        }

        a11yService.addFocusTrap(card);
        card.addEventListener('keydown', keepTabInside);
        // The trap only acts once focus is inside.
        focusIfPossible(card);

        return () => {
            a11yService.removeFocusTrap(card);
            card.removeEventListener('keydown', keepTabInside);
            if (otherCardOpen(card)) {
                return; // the card that replaced this one owns focus now
            }

            const target = focusBeforeCards;
            focusBeforeCards = undefined;
            if (!(target && document.body.contains(target) && focusIfPossible(target))) {
                a11yService.jumpToLastFocused();
            }
        };
    }, []);
}
